import { action, computed } from 'mobx'
import { type WorkspacePostingOpportunity } from './WorkspacePostingOpportunity'
import type { WorkspaceOpportunityDetails } from '@/features/opportunities/workspace-postings-opportunities/components/WorkspaceOpportunityDetailsSheet'

/**
 * View model for a listed workspace posting opportunity.
 */
export class WorkspacePostingOpportunityViewModel {
  constructor(
    public readonly opportunity: WorkspacePostingOpportunity,
    private readonly _isDisabled: () => boolean,
    private readonly _getSelected: () => WorkspacePostingOpportunityViewModel | null,
    private readonly _select: (
      vm: WorkspacePostingOpportunityViewModel,
    ) => void,
  ) {}

  /**
   * The ID of the opportunity.
   */
  @computed
  get opportunityId() {
    return this.opportunity.id
  }

  /**
   * The opportunity's job types, as one line.
   */
  @computed
  get jobTypes() {
    return this.opportunity.jobTypes.join(', ')
  }

  /**
   * The opportunity's requirements, as one line.
   */
  @computed
  get requirements() {
    return this.opportunity.requirements.join(', ')
  }

  /**
   * What the details sheet shows about the opportunity.
   */
  @computed
  get details(): WorkspaceOpportunityDetails {
    return {
      jobTypes: this.jobTypes,
      isApplicant: this.opportunity.isApplicant,
      specialties: this.opportunity.specialties.join(', '),
      description: this.opportunity.description,
      requirements: this.requirements,
      location: this.opportunity.location,
    }
  }

  /**
   * Whether selection is disabled.
   */
  @computed
  get disabled() {
    return this._isDisabled()
  }

  /**
   * Whether this opportunity is the selected one.
   */
  @computed
  get isSelected() {
    return this._getSelected()?.opportunityId === this.opportunityId
  }

  /**
   * Selects this opportunity.
   */
  @action.bound
  select() {
    this._select(this)
  }
}
